import { useEffect, useMemo, useState } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { standardRateNow } from '../lib/taxes.ts';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Reorder } from '../components/Reorder.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';
import { TimelineTab } from './Timeline.tsx';

/**
 * Projects and tasks: a board per project, the person's own tasks, a weekly timesheet, and the
 * hours or fixed prices still to invoice. Everyone on a project's team moves its tasks, ticks
 * their checklists, comments and logs time (by hand or with the timer).
 */

type PState = 'active' | 'on_hold' | 'done' | 'cancelled';
type Billing = 'none' | 'hours' | 'fixed';
interface ProjectRow {
  id: string;
  name: string;
  code: string;
  state: PState;
  billing: Billing;
  starts: string | null;
  deadline: string | null;
  budget_minutes: number;
  hourly_rate: number;
  fixed_price: number;
  members: string[];
  manager: string | null;
  customer: string | null;
  customer_name: string | null;
  manager_name: string | null;
  tasks: number;
  done: number;
  overdue: number;
  minutes: number;
  unbilled: number;
  invoiced: number;
  next_milestone: { name: string; due: string | null } | null;
  mine: boolean;
}
interface Project extends ProjectRow {
  description: string | null;
  tax_rate: number;
  stages: { id: string; name: string; sequence: number; done: boolean; tasks: number }[];
  milestones: { id: string; name: string; due: string | null; reached_at: string | null; tasks: number; done: number }[];
  team: { id: string; name: string; minutes: number; open: number }[];
  bills: { id: string; label: string; amount: number; at: string; invoice: string; state: string; name: string | null }[];
  can_work: boolean;
  today: string;
}
interface TaskRow {
  id: string;
  project: string;
  number: number;
  title: string;
  stage: string;
  assignee: string | null;
  priority: 'normal' | 'high';
  start: string | null;
  deadline: string | null;
  estimate: number;
  position: number;
  done_at: string | null;
  archived: boolean;
  milestone: string | null;
  code: string;
  project_name: string;
  stage_name: string;
  done: boolean;
  assignee_name: string | null;
  milestone_name: string | null;
  checks: number;
  checked: number;
  comments: number;
  spent: number;
  running: boolean;
  /** Unfinished tasks this one waits for. */
  waiting_for: number;
}
interface LinkedTask {
  id: string;
  name: string;
  title: string;
  done: boolean;
}
type TaskDetail = Omit<TaskRow, 'comments'> & {
  description: string | null;
  checklist: { text: string; done: boolean }[];
  comments: { id: string; text: string; at: string; by: string | null; by_name: string | null }[];
  time: { id: string; day: string; minutes: number; note: string | null; billable: boolean; started_at: string | null; person: string; invoice: string | null; person_name: string | null }[];
  stages: { id: string; name: string; done: boolean }[];
  milestones: { id: string; name: string }[];
  billing: Billing;
  project_state: PState;
  can_edit: boolean;
  created_by_name: string | null;
  blocked_by: LinkedTask[];
  blocks: LinkedTask[];
};
interface Running {
  id: string;
  started_at: string;
  task: string | null;
  project: string;
  task_title: string | null;
  number: number | null;
  code: string;
  project_name: string;
}
interface Overview {
  active: number;
  late: number;
  overdue: number;
  my_open: number;
  done_week: number;
  minutes_week: number;
  my_minutes_week: number;
  unbilled: number;
  today: string;
  running: Running | null;
}
interface Setup {
  people: { id: string; name: string }[];
  customers: { id: string; name: string }[];
  projects: { id: string; code: string; name: string; billing: Billing }[];
}

const STATE: Record<PState, { tone: Tone; icon: IconName }> = {
  active: { tone: 'info', icon: 'circle' },
  on_hold: { tone: 'warn', icon: 'pause' },
  done: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
export const taskName = (code: string, n: number) => `${code}-${n}`;
const hm = (m: number) => (m >= 60 ? t('tk.h_m', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') }) : t('tk.m', { m }));
const hours = (m: number) => t('tk.hours', { h: (Math.round((m / 60) * 10) / 10).toLocaleString() });
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const mondayOf = (d: string) => addDays(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));
const shortDay = (d: string) => new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
/** "1:30", "1.5", "1,5", "90m" or "2h" → minutes. A plain number is hours. */
export function parseDuration(text: string): number | null {
  const s = text.trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d{1,2}):(\d{2})$/))) return Number(m[1]) * 60 + Number(m[2]);
  if ((m = s.match(/^(\d{1,4})\s*m(in)?$/))) return Number(m[1]);
  if ((m = s.match(/^(\d{1,2}(?:\.\d+)?)\s*h?$/))) return Math.round(Number(m[1]) * 60);
  return null;
}
const durationText = (m: number) => (m ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}` : '');

function Progress({ value, max, warn, label }: { value: number; max: number; warn?: boolean; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <span className={`tk-progress${warn ? ' warn' : ''}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}

/** The person's running timer, counting up, with a stop button. */
function TimerBar({ running, onChange }: { running: Running | null; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => tick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, [running]);
  if (!running) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(running.started_at)) / 60_000));
  return (
    <div className="tk-timer" role="status">
      <span className="tk-timer-dot" aria-hidden="true" />
      <span className="vat-party">
        <strong>{running.task_title ? `${taskName(running.code, running.number!)} ${running.task_title}` : running.project_name}</strong>
        <span className="small">{t('tk.timer_on', { time: hm(minutes) })}</span>
      </span>
      <Button
        size="sm"
        icon="pause"
        busy={busy}
        onClick={() =>
          void run(
            async () => {
              await action('project.timer_stop', {});
              await onChange();
            },
            t('tk.timer_saved', { time: hm(Math.max(1, minutes)) }),
          )
        }
      >
        {t('tk.stop')}
      </Button>
    </div>
  );
}

// ---- the home page ------------------------------------------------------------------------------------

type HomeTab = 'projects' | 'mine' | 'timesheet' | 'invoice' | 'results';

export function TasksHome() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const navigate = useNavigate();
  const tabs: { key: HomeTab; icon: IconName; show: boolean }[] = [
    { key: 'projects', icon: 'layers', show: true },
    { key: 'mine', icon: 'check', show: can('project.work') },
    { key: 'timesheet', icon: 'clock', show: can('project.work') },
    { key: 'invoice', icon: 'invoice', show: can('project.invoice') },
    { key: 'results', icon: 'chart', show: can('project.reports') },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'projects') as HomeTab;
  const { data: o, reload } = useAction<Overview>('project.overview');
  const { data: setup } = useAction<Setup>('project.setup');
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState(false);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const openTask = params.get('t');
  return (
    <Shell>
      <div className="page reports tk-page">
        <div className="page-head">
          <div>
            <h1>{t('tk.title')}</h1>
            <p className="muted">{t('tk.subtitle')}</p>
          </div>
          {can('project.manage') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setForm(true)}>
                {t('tk.new_project')}
              </Button>
            </div>
          ) : null}
        </div>
        {o ? <TimerBar running={o.running} onChange={changed} /> : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('tk.at_a_glance')}>
            <Kpi label={t('tk.k_active')} value={String(o.active)} hint={o.late ? tn('tk.k_late', o.late) : t('tk.k_on_time')} warn={o.late > 0} />
            <Kpi label={t('tk.k_mine')} value={String(o.my_open)} hint={o.overdue ? tn('tk.k_overdue', o.overdue) : t('tk.k_none_overdue')} warn={o.overdue > 0} />
            <Kpi label={t('tk.k_week')} value={hm(o.minutes_week)} hint={t('tk.k_week_hint', { mine: hm(o.my_minutes_week), done: o.done_week })} />
            {can('project.invoice') ? <Kpi label={t('tk.k_unbilled')} value={money(o.unbilled)} hint={t('tk.k_unbilled_hint')} /> : null}
          </section>
        )}
        <div className="segmented tabs" role="tablist" aria-label={t('tk.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'projects' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`tk.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {tab === 'projects' ? (
          <ProjectsTab key={version} />
        ) : tab === 'mine' ? (
          <MineTab key={version} onOpen={(id) => set({ t: id })} onChange={changed} />
        ) : tab === 'timesheet' ? (
          setup ? (
            <TimesheetTab key={version} setup={setup} onChange={changed} />
          ) : (
            <Skeleton h={300} />
          )
        ) : tab === 'invoice' ? (
          <InvoiceTab key={version} onChange={changed} />
        ) : (
          <ResultsTab />
        )}
      </div>
      {openTask ? <TaskDialog key={`${openTask}-${version}`} id={openTask} onClose={() => set({ t: null })} onChange={changed} /> : null}
      {form && setup ? (
        <ProjectForm
          setup={setup}
          onClose={() => setForm(false)}
          onSaved={(id) => {
            setForm(false);
            navigate(`/tasks/p/${id}`);
          }}
        />
      ) : null}
    </Shell>
  );
}

const PFILTERS = ['active', 'mine', 'on_hold', 'done', 'all'] as const;

function ProjectsTab() {
  const [filter, setFilter] = useState<(typeof PFILTERS)[number]>('active');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<ProjectRow[]>('project.projects', { filter, ...(q ? { search: q } : {}) });
  const today = todayIso();
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle mkt-filters" role="radiogroup" aria-label={t('tk.show')}>
          {PFILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`tk.pf_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('tk.search_projects')} aria-label={t('tk.search_projects')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : data.length ? (
        <ul className="tk-cards">
          {data.map((p) => {
            const late = p.deadline && p.deadline < today && p.state === 'active';
            const over = p.budget_minutes > 0 && p.minutes > p.budget_minutes;
            return (
              <li key={p.id}>
                <Link to={`/tasks/p/${p.id}`} className="tk-card">
                  <div className="tk-card-head">
                    <span className="tk-code">{p.code}</span>
                    {p.state !== 'active' ? (
                      <StatusBadge tone={STATE[p.state].tone} icon={STATE[p.state].icon} label={t(`tk.st_${p.state}` as MessageKey)} />
                    ) : p.overdue ? (
                      <StatusBadge tone="danger" icon="alert" label={tn('tk.n_overdue', p.overdue)} />
                    ) : null}
                  </div>
                  <strong className="tk-card-title">{p.name}</strong>
                  <span className="muted small">{[p.customer_name ?? t('tk.internal'), p.manager_name].filter(Boolean).join(' · ')}</span>
                  <div className="tk-card-meter">
                    <span className="small">{t('tk.tasks_done', { done: p.done, total: p.tasks })}</span>
                    <Progress value={p.done} max={p.tasks} label={t('tk.tasks_done', { done: p.done, total: p.tasks })} />
                  </div>
                  {p.budget_minutes ? (
                    <div className="tk-card-meter">
                      <span className={`small${over ? ' tk-warn' : ''}`}>{t('tk.of_budget', { spent: hours(p.minutes), budget: hours(p.budget_minutes) })}</span>
                      <Progress value={p.minutes} max={p.budget_minutes} warn={over} label={t('tk.of_budget', { spent: hours(p.minutes), budget: hours(p.budget_minutes) })} />
                    </div>
                  ) : (
                    <span className="small muted">{t('tk.logged', { time: hours(p.minutes) })}</span>
                  )}
                  <div className="tk-card-foot small">
                    {p.deadline ? (
                      <span className={late ? 'tk-warn' : 'muted'}>
                        <Icon name="calendar" size={14} /> {dayText(p.deadline)}
                      </span>
                    ) : (
                      <span className="muted">{t('tk.no_deadline')}</span>
                    )}
                    {p.next_milestone ? (
                      <span className="muted">
                        <Icon name="star" size={14} /> {p.next_milestone.name}
                      </span>
                    ) : null}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="layers" title={t('tk.no_projects')} body={t('tk.no_projects_hint')} />
      )}
    </section>
  );
}

function MineTab({ onOpen, onChange }: { onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [all, setAll] = useState(false);
  const { data, reload } = useAction<TaskRow[]>('project.tasks', { mine: true, open: !all });
  const today = todayIso();
  const groups = useMemo(() => {
    const out: { key: string; title: string; rows: TaskRow[] }[] = [
      { key: 'late', title: t('tk.g_late'), rows: [] },
      { key: 'soon', title: t('tk.g_week'), rows: [] },
      { key: 'later', title: t('tk.g_later'), rows: [] },
      { key: 'done', title: t('tk.g_done'), rows: [] },
    ];
    for (const r of data ?? []) {
      const g = r.done ? 3 : r.deadline && r.deadline < today ? 0 : r.deadline && r.deadline <= addDays(today, 7) ? 1 : 2;
      out[g]!.rows.push(r);
    }
    return out.filter((g) => g.rows.length);
  }, [data, today]);
  return (
    <section className="panel">
      <div className="toolbar">
        <p className="muted small">{t('tk.mine_hint')}</p>
        <label className="check-row">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          <span>{t('tk.show_done')}</span>
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : groups.length ? (
        groups.map((g) => (
          <div key={g.key} className="tk-group">
            <h3 className="rep-sub">{g.title}</h3>
            <ul className="tk-rows">
              {g.rows.map((r) => (
                <li key={r.id} className={r.done ? 'done' : ''}>
                  <button className="tk-row-main" onClick={() => onOpen(r.id)}>
                    <span className="vat-party">
                      <strong>
                        {r.priority === 'high' && !r.done ? <Icon name="alert" size={14} /> : null} {r.title}
                      </strong>
                      <span className="muted small">
                        {taskName(r.code, r.number)} · {r.project_name} · {r.stage_name}
                      </span>
                    </span>
                    <span className={`small tabular${r.deadline && r.deadline < today && !r.done ? ' tk-warn' : ' muted'}`}>{r.deadline ? dayText(r.deadline) : ''}</span>
                  </button>
                  {!r.done ? (
                    <IconButton
                      icon={r.running ? 'pause' : 'clock'}
                      label={r.running ? t('tk.stop') : t('tk.start_timer')}
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await action(r.running ? 'project.timer_stop' : 'project.timer_start', r.running ? {} : { task: r.id });
                          await reload();
                          await onChange();
                        })
                      }
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <EmptyState icon="checkCircle" title={t('tk.mine_none')} body={t('tk.mine_none_hint')} />
      )}
    </section>
  );
}

// ---- timesheet ----------------------------------------------------------------------------------------

interface Sheet {
  person: string;
  days: string[];
  today: string;
  lines: { project: string; task: string | null; code: string; project_name: string; number: number | null; task_title: string | null; days: Record<string, number>; total: number }[];
  entries: {
    id: string;
    project: string;
    task: string | null;
    day: string;
    minutes: number;
    note: string | null;
    billable: boolean;
    started_at: string | null;
    invoice: string | null;
    code: string;
    project_name: string;
    number: number | null;
    task_title: string | null;
  }[];
  totals: Record<string, number>;
  running: Running | null;
}

function TimesheetTab({ setup, onChange }: { setup: Setup; onChange: () => Promise<void> }) {
  const { can, me } = useSession();
  const [from, setFrom] = useState(() => mondayOf(todayIso()));
  const [person, setPerson] = useState('');
  const { data, reload } = useAction<Sheet>('project.timesheet', { from, ...(person ? { person } : {}) });
  const [edit, setEdit] = useState<Partial<TimeForm> | null>(null);
  const total = data ? Object.values(data.totals).reduce((s, n) => s + n, 0) : 0;
  const own = !person || person === me?.userId;
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="row-actions">
          <IconButton icon="back" label={t('tk.prev_week')} onClick={() => setFrom(addDays(from, -7))} />
          <Button size="sm" variant="ghost" onClick={() => setFrom(mondayOf(todayIso()))}>
            {t('tk.this_week')}
          </Button>
          <IconButton icon="chevronRight" label={t('tk.next_week')} onClick={() => setFrom(addDays(from, 7))} />
          <strong>{t('tk.week_of', { from: dayText(from), to: dayText(addDays(from, 6)) })}</strong>
        </div>
        <span className="row-actions">
          {can('project.manage') ? (
            <select className="input tk-person" aria-label={t('tk.person')} value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">{t('tk.me')}</option>
              {setup.people
                .filter((p) => p.id !== me?.userId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          ) : null}
          {own && setup.projects.length ? (
            <Button icon="plus" onClick={() => setEdit({ day: data?.days.includes(todayIso()) ? todayIso() : from })}>
              {t('tk.log_time')}
            </Button>
          ) : null}
        </span>
      </div>
      {!data ? (
        <Skeleton h={260} />
      ) : (
        <>
          <div className="tk-sheet-wrap">
            <table className="tk-sheet">
              <thead>
                <tr>
                  <th scope="col">{t('tk.task')}</th>
                  {data.days.map((d) => (
                    <th key={d} scope="col" className={d === data.today ? 'today' : ''}>
                      {shortDay(d)}
                    </th>
                  ))}
                  <th scope="col">{t('tk.total')}</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l) => (
                  <tr key={`${l.project}:${l.task ?? ''}`}>
                    <th scope="row">
                      <span className="vat-party">
                        <span>{l.task_title ?? t('tk.no_task')}</span>
                        <span className="muted small">{l.number ? `${taskName(l.code, l.number)} · ${l.project_name}` : l.project_name}</span>
                      </span>
                    </th>
                    {data.days.map((d) => (
                      <td key={d} className={`tabular${d === data.today ? ' today' : ''}`}>
                        {own ? (
                          <button className="tk-cell" aria-label={t('tk.add_on', { day: dayText(d) })} onClick={() => setEdit({ project: l.project, task: l.task ?? '', day: d })}>
                            {l.days[d] ? durationText(l.days[d]!) : <span className="muted">·</span>}
                          </button>
                        ) : l.days[d] ? (
                          durationText(l.days[d]!)
                        ) : (
                          ''
                        )}
                      </td>
                    ))}
                    <td className="tabular">
                      <strong>{durationText(l.total)}</strong>
                    </td>
                  </tr>
                ))}
                {!data.lines.length ? (
                  <tr>
                    <td colSpan={9} className="muted">
                      {t('tk.sheet_empty')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t('tk.total')}</th>
                  {data.days.map((d) => (
                    <td key={d} className={`tabular${d === data.today ? ' today' : ''}`}>
                      {durationText(data.totals[d] ?? 0)}
                    </td>
                  ))}
                  <td className="tabular">
                    <strong>{durationText(total)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {data.entries.length ? (
            <details className="tk-entries">
              <summary>{tn('tk.n_entries', data.entries.length)}</summary>
              <ul className="shop-ways">
                {data.entries.map((e) => (
                  <li key={e.id}>
                    <span className="vat-party">
                      <span>{e.task_title ?? e.project_name}</span>
                      <span className="muted small">
                        {[dayText(e.day), e.number ? taskName(e.code, e.number) : e.code, e.note, e.started_at ? t('tk.running') : null, e.invoice ? t('tk.invoiced') : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    <strong className="tabular">{e.started_at ? '…' : durationText(e.minutes)}</strong>
                    {own && !e.invoice && !e.started_at ? (
                      <IconButton
                        icon="edit"
                        label={t('tk.edit')}
                        onClick={() => setEdit({ id: e.id, project: e.project, task: e.task ?? '', day: e.day, time: durationText(e.minutes), note: e.note ?? '', billable: e.billable })}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      )}
      {edit ? (
        <TimeDialog
          initial={edit}
          projects={setup.projects}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

interface TimeForm {
  id?: string;
  project: string;
  task: string;
  day: string;
  time: string;
  note: string;
  billable: boolean;
}

function TimeDialog({
  initial,
  projects,
  fixedTask,
  onClose,
  onSaved,
}: {
  initial: Partial<TimeForm>;
  projects: Setup['projects'];
  fixedTask?: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [f, setF] = useState<TimeForm>({ project: projects[0]?.id ?? '', task: '', day: todayIso(), time: '', note: '', billable: true, ...initial });
  const [tried, setTried] = useState(false);
  const { data: list } = useAction<TaskRow[]>('project.tasks', { project: f.project || null });
  const p = projects.find((x) => x.id === f.project);
  const minutes = parseDuration(f.time);
  const set = <K extends keyof TimeForm>(k: K, v: TimeForm[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      title={f.id ? t('tk.edit_time') : t('tk.log_time')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {f.id ? (
            <Button
              variant="ghost"
              icon="trash"
              onClick={() =>
                void run(async () => {
                  await action('project.remove_time', { id: f.id });
                  await onSaved();
                }, t('tk.time_removed'))
              }
            >
              {t('tk.remove')}
            </Button>
          ) : null}
          <span className="foot-actions">
            <Button
              variant="primary"
              icon="check"
              busy={busy}
              onClick={() => {
                setTried(true);
                if (!minutes || !f.project) return;
                void run(
                  async () => {
                    await action('project.log_time', { id: f.id ?? null, project: f.project, task: f.task || null, day: f.day, minutes, note: f.note.trim() || null, billable: f.billable });
                    await onSaved();
                  },
                  t('tk.time_saved', { time: hm(minutes) }),
                );
              }}
            >
              {t('tk.save')}
            </Button>
          </span>
        </>
      }
    >
      <div className="form-stack">
        {!fixedTask ? (
          <>
            <Field label={t('tk.project')}>
              <select className="input" value={f.project} disabled={!!f.id} onChange={(e) => setF((x) => ({ ...x, project: e.target.value, task: '' }))}>
                {projects.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.code} · {x.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('tk.task')} hint={t('tk.optional')}>
              <select className="input" value={f.task} onChange={(e) => set('task', e.target.value)}>
                <option value="">{t('tk.no_task')}</option>
                {(list ?? [])
                  .filter((x) => !x.done || x.id === f.task)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {taskName(x.code, x.number)} {x.title}
                    </option>
                  ))}
              </select>
            </Field>
          </>
        ) : null}
        <div className="form-grid">
          <Field label={t('tk.day')}>
            <input className="input" type="date" value={f.day} max={addDays(todayIso(), 1)} onChange={(e) => e.target.value && set('day', e.target.value)} />
          </Field>
          <Field label={t('tk.how_long')} hint={t('tk.how_long_hint')} error={tried && !minutes ? t('tk.e_time') : null} required>
            <input className="input" inputMode="decimal" value={f.time} placeholder="1:30" onChange={(e) => set('time', e.target.value)} data-autofocus />
          </Field>
        </div>
        <Field label={t('tk.note')} hint={t('tk.optional')}>
          <input className="input" value={f.note} maxLength={300} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {p?.billing === 'hours' ? (
          <div className="setting-row">
            <div>
              <strong>{t('tk.billable')}</strong>
              <p className="muted">{t('tk.billable_hint')}</p>
            </div>
            <Switch checked={f.billable} onChange={(v) => set('billable', v)} label={t('tk.billable')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- invoicing and results ----------------------------------------------------------------------------

function InvoiceTab({ onChange }: { onChange: () => Promise<void> }) {
  const { data, reload } = useAction<(ProjectRow & { amount: number })[]>('project.to_invoice');
  const [pick, setPick] = useState<(ProjectRow & { amount: number }) | null>(null);
  return (
    <section className="panel">
      <p className="muted small">{t('tk.invoice_hint')}</p>
      {!data ? (
        <Skeleton h={200} />
      ) : (
        <Table
          head={[t('tk.project'), t('tk.billing'), t('tk.to_invoice'), '']}
          left={[0]}
          phoneHide={[1]}
          empty={t('tk.nothing_to_invoice')}
          rows={data.map((p) => ({
            key: p.id,
            cells: [
              <span className="vat-party" key="p">
                <Link className="link" to={`/tasks/p/${p.id}`}>
                  {p.code} · {p.name}
                </Link>
                <span className="muted small">{p.customer_name}</span>
              </span>,
              p.billing === 'hours' ? t('tk.by_hours', { rate: money(p.hourly_rate) }) : t('tk.fixed_of', { price: money(p.fixed_price) }),
              <span className="vat-party" key="a">
                <strong className="tabular">{money(p.amount)}</strong>
                <span className="muted small">{p.billing === 'hours' ? hm(p.unbilled) : t('tk.left_of_price')}</span>
              </span>,
              <Button key="b" size="sm" icon="invoice" onClick={() => setPick(p)}>
                {t('tk.invoice')}
              </Button>,
            ],
          }))}
        />
      )}
      {pick ? (
        <InvoiceDialog
          p={pick}
          left={pick.amount}
          onClose={() => setPick(null)}
          onDone={async () => {
            setPick(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function InvoiceDialog({
  p,
  left,
  onClose,
  onDone,
}: {
  p: Pick<ProjectRow, 'id' | 'name' | 'code' | 'billing' | 'customer_name' | 'unbilled' | 'hourly_rate' | 'fixed_price'>;
  left: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [until, setUntil] = useState(todayIso());
  const [amount, setAmount] = useState((left / 100).toFixed(2));
  const [label, setLabel] = useState('');
  const typed = parseMoney(amount);
  const bad = p.billing === 'fixed' && (typed === null || typed <= 0 || typed > left);
  return (
    <Dialog
      title={t('tk.invoice_title', { name: `${p.code} · ${p.name}` })}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="invoice"
          busy={busy}
          disabled={bad}
          onClick={() =>
            void run(async () => {
              await action('project.invoice', p.billing === 'hours' ? { project: p.id, until } : { project: p.id, amount: typed, label: label.trim() || null });
              await onDone();
            }, t('tk.invoice_made'))
          }
        >
          {t('tk.make_draft')}
        </Button>
      }
    >
      <div className="form-stack">
        <p>{t('tk.invoice_for', { customer: p.customer_name ?? '' })}</p>
        {p.billing === 'hours' ? (
          <Field label={t('tk.hours_until')} hint={t('tk.hours_until_hint', { rate: money(p.hourly_rate) })}>
            <input className="input" type="date" value={until} max={todayIso()} onChange={(e) => e.target.value && setUntil(e.target.value)} />
          </Field>
        ) : (
          <>
            <Field label={t('tk.amount')} hint={t('tk.amount_hint', { left: money(left), price: money(p.fixed_price) })} error={bad ? t('tk.e_amount', { left: money(left) }) : null}>
              <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label={t('tk.line_text')} hint={t('tk.optional')}>
              <input className="input" value={label} maxLength={160} placeholder={t('tk.line_example', { name: p.name })} onChange={(e) => setLabel(e.target.value)} />
            </Field>
          </>
        )}
        <p className="muted small">{t('tk.draft_note')}</p>
      </div>
    </Dialog>
  );
}

interface Report {
  by_project: { id: string; code: string; name: string; billing: Billing; budget_minutes: number; minutes: number; billable: number | null; all_minutes: number }[];
  by_person: { id: string; name: string; minutes: number; billable: number | null; days: number }[];
  by_day: { day: string; minutes: number }[];
  tasks_done: number;
  tasks_late: number;
}

function ResultsTab() {
  const [from, setFrom] = useState(() => addDays(todayIso(), -29));
  const [to, setTo] = useState(todayIso);
  const { data } = useAction<Report>('project.report', { from, to });
  const total = data?.by_person.reduce((s, p) => s + p.minutes, 0) ?? 0;
  const billable = data?.by_person.reduce((s, p) => s + (p.billable ?? 0), 0) ?? 0;
  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = from; d <= to && out.length < 400; d = addDays(d, 1)) out.push(d);
    return out;
  }, [from, to]);
  const csv = () =>
    data &&
    downloadCsv(
      'project-hours.csv',
      [t('tk.project'), t('tk.r_hours'), t('tk.r_billable'), t('tk.budget')],
      data.by_project.map((p) => [`${p.code} ${p.name}`, (p.minutes / 60).toFixed(2), ((p.billable ?? 0) / 60).toFixed(2), (p.budget_minutes / 60).toFixed(2)]),
    );
  return (
    <div className="form-stack">
      <section className="panel">
        <div className="toolbar">
          <div className="row-actions">
            <Field label={t('tk.from')}>
              <input className="input" type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            </Field>
            <Field label={t('tk.to')}>
              <input className="input" type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
            </Field>
          </div>
          <IconButton icon="download" label={t('tk.csv')} onClick={csv} disabled={!data?.by_project.length} />
        </div>
        {!data ? (
          <Skeleton h={200} />
        ) : (
          <>
            <section className="rep-kpis">
              <Kpi label={t('tk.r_hours')} value={hm(total)} hint={tn('tk.n_people', data.by_person.length)} />
              <Kpi label={t('tk.r_billable')} value={total ? `${Math.round((billable / total) * 100)}%` : '—'} hint={t('tk.r_billable_hint', { time: hm(billable) })} />
              <Kpi label={t('tk.r_done')} value={String(data.tasks_done)} hint={data.tasks_late ? tn('tk.r_late', data.tasks_late) : t('tk.r_on_time')} warn={data.tasks_late > 0} />
            </section>
            <Bars
              small
              items={days.map((d) => ({
                key: d,
                label: shortDay(d),
                value: data.by_day.find((x) => x.day === d)?.minutes ?? 0,
                tip: `${dayText(d)}: ${hm(data.by_day.find((x) => x.day === d)?.minutes ?? 0)}`,
                weekend: [0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay()),
              }))}
            />
          </>
        )}
      </section>
      {data ? (
        <div className="shop-detail">
          <section className="panel">
            <h2 className="rep-sub">{t('tk.r_by_project')}</h2>
            <Table
              head={[t('tk.project'), t('tk.r_hours'), t('tk.budget')]}
              left={[0]}
              empty={t('tk.r_nothing')}
              rows={data.by_project.map((p) => ({
                key: p.id,
                cells: [
                  `${p.code} · ${p.name}`,
                  hm(p.minutes),
                  p.budget_minutes ? (
                    <span key="b" className={p.all_minutes > p.budget_minutes ? 'tk-warn' : ''}>
                      {Math.round((p.all_minutes / p.budget_minutes) * 100)}%
                    </span>
                  ) : (
                    '—'
                  ),
                ],
              }))}
            />
          </section>
          <section className="panel">
            <h2 className="rep-sub">{t('tk.r_by_person')}</h2>
            <Table
              head={[t('tk.person'), t('tk.r_hours'), t('tk.r_per_day')]}
              left={[0]}
              empty={t('tk.r_nothing')}
              rows={data.by_person.map((p) => ({ key: p.id, cells: [p.name, hm(p.minutes), hm(Math.round(p.minutes / Math.max(1, p.days)))] }))}
            />
          </section>
        </div>
      ) : null}
    </div>
  );
}

// ---- one project --------------------------------------------------------------------------------------

type PTab = 'board' | 'list' | 'timeline' | 'milestones' | 'time' | 'settings';

export function TaskProject() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: p, reload } = useAction<Project>('project.project', { id });
  const { data: o, reload: reloadOverview } = useAction<Overview>('project.overview');
  const { data: setup } = useAction<Setup>('project.setup');
  const { data: list, reload: reloadTasks } = useAction<TaskRow[]>('project.tasks', { project: id });
  const [form, setForm] = useState(false);
  const [newTask, setNewTask] = useState<string | null>(null);
  const [invoicing, setInvoicing] = useState(false);
  const tab = (['board', 'list', 'timeline', 'milestones', 'time', 'settings'].includes(params.get('tab') ?? '') ? params.get('tab') : 'board') as PTab;
  const set = (next: Record<string, string | null>) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? q.delete(k) : q.set(k, v);
    setParams(q, { replace: true });
  };
  const changed = async () => {
    await Promise.all([reload(), reloadTasks(), reloadOverview()]);
  };
  if (!p || !list) {
    return (
      <Shell>
        <div className="page">
          <Skeleton h={400} />
        </div>
      </Shell>
    );
  }
  const today = p.today;
  const left = p.billing === 'hours' ? Math.round((p.unbilled * p.hourly_rate) / 60) : Math.max(0, p.fixed_price - p.invoiced);
  const working = p.state === 'active' && p.can_work;
  return (
    <Shell>
      <div className="page reports tk-page">
        <nav className="tk-crumbs" aria-label={t('tk.crumbs')}>
          <Link to="/tasks">{t('tk.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{p.code}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1>
              {p.name} {p.state !== 'active' ? <StatusBadge tone={STATE[p.state].tone} icon={STATE[p.state].icon} label={t(`tk.st_${p.state}` as MessageKey)} /> : null}
            </h1>
            <p className="muted">
              {[p.code, p.customer_name ?? t('tk.internal'), p.manager_name ? t('tk.led_by', { name: p.manager_name }) : null, p.deadline ? t('tk.due', { day: dayText(p.deadline) }) : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <div className="page-actions">
            {can('project.invoice') && p.billing !== 'none' && left > 0 ? (
              <Button icon="invoice" onClick={() => setInvoicing(true)}>
                {t('tk.invoice_amount', { amount: money(left) })}
              </Button>
            ) : null}
            {can('project.manage') ? (
              <Button variant="ghost" icon="edit" onClick={() => setForm(true)}>
                {t('tk.edit')}
              </Button>
            ) : null}
            {working ? (
              <Button variant="primary" icon="plus" onClick={() => setNewTask(p.stages[0]!.id)}>
                {t('tk.new_task')}
              </Button>
            ) : null}
          </div>
        </div>
        {o ? <TimerBar running={o.running} onChange={changed} /> : null}
        {p.state !== 'active' ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t(`tk.state_note_${p.state}` as MessageKey)}</span>
            {can('project.manage') ? (
              <Button
                size="sm"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    await action('project.set_state', { id: p.id, state: 'active' });
                    await changed();
                  }, t('tk.reopened'))
                }
              >
                {t('tk.reactivate')}
              </Button>
            ) : null}
          </p>
        ) : null}
        <section className="rep-kpis" aria-label={t('tk.at_a_glance')}>
          <Kpi label={t('tk.k_tasks')} value={`${p.done}/${p.tasks}`} hint={p.overdue ? tn('tk.n_overdue', p.overdue) : t('tk.k_none_overdue')} warn={p.overdue > 0} />
          <Kpi
            label={t('tk.k_time')}
            value={hm(p.minutes)}
            hint={p.budget_minutes ? t('tk.k_budget_pct', { pct: Math.round((p.minutes / p.budget_minutes) * 100), budget: hours(p.budget_minutes) }) : t('tk.k_no_budget')}
            warn={p.budget_minutes > 0 && p.minutes > p.budget_minutes}
          />
          <Kpi label={t('tk.k_next')} value={p.next_milestone?.name ?? '—'} hint={p.next_milestone?.due ? dayText(p.next_milestone.due) : t('tk.k_no_milestone')} />
          {p.billing !== 'none' && can('project.invoice') ? (
            <Kpi label={t('tk.k_billed')} value={money(p.invoiced)} hint={p.billing === 'hours' ? t('tk.k_unbilled_hours', { time: hm(p.unbilled) }) : t('tk.k_left', { left: money(left) })} />
          ) : null}
        </section>
        <div className="segmented tabs" role="tablist" aria-label={t('tk.sections')}>
          {(['board', 'list', 'timeline', 'milestones', 'time', 'settings'] as PTab[])
            .filter((x) => x !== 'settings' || can('project.manage'))
            .map((x) => (
              <button key={x} role="tab" aria-selected={tab === x} className={tab === x ? 'active' : ''} onClick={() => set({ tab: x === 'board' ? null : x })}>
                <Icon name={({ board: 'layers', list: 'list', timeline: 'calendar', milestones: 'star', time: 'clock', settings: 'settings' } as const)[x]} size={16} />
                {t(`tk.ptab_${x}` as MessageKey)}
              </button>
            ))}
        </div>
        {tab === 'board' ? (
          <Board p={p} tasks={list} today={today} working={working} onOpen={(tid) => set({ t: tid })} onAdd={(stage) => setNewTask(stage)} onChange={changed} />
        ) : tab === 'list' ? (
          <TaskTable tasks={list} today={today} onOpen={(tid) => set({ t: tid })} />
        ) : tab === 'timeline' ? (
          <TimelineTab project={p.id} onOpen={(tid) => set({ t: tid })} onChange={changed} />
        ) : tab === 'milestones' ? (
          <Milestones p={p} onChange={changed} />
        ) : tab === 'time' ? (
          <ProjectTime p={p} setup={setup} onChange={changed} />
        ) : (
          <ProjectSettings p={p} onChange={changed} />
        )}
      </div>
      {params.get('t') ? <TaskDialog key={params.get('t')} id={params.get('t')!} board={list} onClose={() => set({ t: null })} onChange={changed} /> : null}
      {newTask && setup ? (
        <TaskForm
          project={p}
          stage={newTask}
          people={setup.people}
          onClose={() => setNewTask(null)}
          onSaved={async (tid) => {
            setNewTask(null);
            await changed();
            set({ t: tid });
          }}
        />
      ) : null}
      {form && setup ? (
        <ProjectForm
          setup={setup}
          project={p}
          onClose={() => setForm(false)}
          onSaved={async () => {
            setForm(false);
            await changed();
          }}
        />
      ) : null}
      {invoicing ? (
        <InvoiceDialog
          p={p}
          left={left}
          onClose={() => setInvoicing(false)}
          onDone={async () => {
            setInvoicing(false);
            await changed();
          }}
        />
      ) : null}
    </Shell>
  );
}

function TaskCard({ r, today, onOpen, draggable, onDragStart }: { r: TaskRow; today: string; onOpen: () => void; draggable?: boolean; onDragStart?: (e: React.DragEvent) => void }) {
  const late = !r.done && r.deadline && r.deadline < today;
  return (
    <article className={`tk-task${r.priority === 'high' && !r.done ? ' high' : ''}${r.done ? ' done' : ''}`} draggable={draggable} onDragStart={onDragStart} data-id={r.id}>
      <button className="tk-task-main" onClick={onOpen}>
        <span className="muted small tk-task-no">
          {taskName(r.code, r.number)}
          {r.running ? <span className="tk-timer-dot" aria-label={t('tk.running')} /> : null}
        </span>
        <strong>{r.title}</strong>
        {r.milestone_name ? (
          <span className="small muted">
            <Icon name="star" size={12} /> {r.milestone_name}
          </span>
        ) : null}
        <span className="tk-task-meta small">
          {r.waiting_for && !r.done ? (
            <span className="tk-warn" title={tn('tk.waits_for_n', r.waiting_for)}>
              <Icon name="lock" size={12} /> {tn('tk.waits_for_n', r.waiting_for)}
            </span>
          ) : null}
          {r.deadline ? (
            <span className={late ? 'tk-warn' : 'muted'}>
              <Icon name="calendar" size={12} /> {dayText(r.deadline)}
            </span>
          ) : null}
          {r.checks ? (
            <span className="muted">
              <Icon name="check" size={12} /> {r.checked}/{r.checks}
            </span>
          ) : null}
          {r.comments ? (
            <span className="muted">
              <Icon name="note" size={12} /> {r.comments}
            </span>
          ) : null}
          {r.spent || r.estimate ? (
            <span className={r.estimate && r.spent > r.estimate ? 'tk-warn' : 'muted'}>
              <Icon name="clock" size={12} /> {r.estimate ? `${hm(r.spent)} / ${hm(r.estimate)}` : hm(r.spent)}
            </span>
          ) : null}
          <span className="tk-avatar" title={r.assignee_name ?? t('tk.nobody')} aria-label={r.assignee_name ?? t('tk.nobody')}>
            {r.assignee_name ? r.assignee_name.slice(0, 1) : '?'}
          </span>
        </span>
      </button>
    </article>
  );
}

function Board({
  p,
  tasks,
  today,
  working,
  onOpen,
  onAdd,
  onChange,
}: {
  p: Project;
  tasks: TaskRow[];
  today: string;
  working: boolean;
  onOpen: (id: string) => void;
  onAdd: (stage: string) => void;
  onChange: () => Promise<void>;
}) {
  const { fail } = useUi();
  const [rows, setRows] = useState(tasks);
  const [who, setWho] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<{ stage: string; before: string | null } | null>(null);
  useEffect(() => setRows(tasks), [tasks]);
  const shown = rows.filter((r) => !who || (who === '-' ? !r.assignee : r.assignee === who));
  const drop = async (stage: string, before: string | null) => {
    const id = dragging;
    setDragging(null);
    setOver(null);
    if (!id) return;
    const column = rows.filter((r) => r.stage === stage && r.id !== id).sort((a, b) => a.position - b.position);
    const at = before ? column.findIndex((r) => r.id === before) : column.length;
    const moving = rows.find((r) => r.id === id)!;
    column.splice(at < 0 ? column.length : at, 0, { ...moving, stage });
    const order = column.map((r) => r.id);
    const col = p.stages.find((s) => s.id === stage)!;
    // Shown at once; the server's answer follows.
    setRows((all) => all.map((r) => (r.id === id ? { ...r, stage, done: col.done, stage_name: col.name } : r)).map((r) => (order.includes(r.id) ? { ...r, position: order.indexOf(r.id) + 1 } : r)));
    try {
      await action('project.move_task', { id, stage, order });
    } catch (e) {
      fail(e);
    }
    await onChange();
  };
  return (
    <>
      <div className="toolbar tk-board-tools">
        <select className="input tk-person" aria-label={t('tk.assignee')} value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">{t('tk.everyone')}</option>
          {p.team.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
          <option value="-">{t('tk.nobody')}</option>
        </select>
        {working ? <span className="muted small tk-drag-hint">{t('tk.drag_hint')}</span> : null}
      </div>
      <div className="tk-board" style={{ '--cols': p.stages.length } as React.CSSProperties}>
        {p.stages.map((s) => {
          const cards = shown.filter((r) => r.stage === s.id).sort((a, b) => a.position - b.position);
          return (
            <section
              key={s.id}
              className={`tk-col${over?.stage === s.id ? ' over' : ''}${s.done ? ' done' : ''}`}
              aria-label={s.name}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                if (over?.stage !== s.id || over.before !== null) {
                  const target = (e.target as HTMLElement).closest('[data-id]')?.getAttribute('data-id') ?? null;
                  setOver({ stage: s.id, before: target === dragging ? null : target });
                }
              }}
              onDragLeave={(e) => {
                if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void drop(s.id, over?.stage === s.id ? over.before : null);
              }}
            >
              <header className="tk-col-head">
                <strong>{s.name}</strong>
                <span className="muted small">{cards.length}</span>
                {working && !s.done ? <IconButton icon="plus" label={t('tk.add_to', { column: s.name })} onClick={() => onAdd(s.id)} /> : null}
              </header>
              <div className="tk-col-body">
                {cards.map((r) => (
                  <div key={r.id} className={over?.before === r.id && over.stage === s.id ? 'tk-drop-before' : ''}>
                    <TaskCard
                      r={r}
                      today={today}
                      onOpen={() => onOpen(r.id)}
                      draggable={working}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', r.id);
                        setDragging(r.id);
                      }}
                    />
                  </div>
                ))}
                {!cards.length ? <p className="muted small tk-col-empty">{s.done ? t('tk.col_done_empty') : t('tk.col_empty')}</p> : null}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

function TaskTable({ tasks, today, onOpen }: { tasks: TaskRow[]; today: string; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const shown = tasks.filter((r) => !q || `${taskName(r.code, r.number)} ${r.title} ${r.assignee_name ?? ''}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="panel">
      <div className="toolbar">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('tk.search_tasks')} aria-label={t('tk.search_tasks')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <IconButton
          icon="download"
          label={t('tk.csv')}
          onClick={() =>
            downloadCsv(
              'tasks.csv',
              [t('tk.task'), t('tk.column'), t('tk.assignee'), t('tk.deadline'), t('tk.estimate'), t('tk.spent')],
              shown.map((r) => [`${taskName(r.code, r.number)} ${r.title}`, r.stage_name, r.assignee_name ?? '', r.deadline ?? '', (r.estimate / 60).toFixed(2), (r.spent / 60).toFixed(2)]),
            )
          }
        />
      </div>
      <Table
        head={[t('tk.task'), t('tk.column'), t('tk.assignee'), t('tk.deadline'), t('tk.spent')]}
        left={[0, 2]}
        phoneHide={[1, 2, 4]}
        empty={t('tk.no_tasks')}
        rows={shown.map((r) => ({
          key: r.id,
          onClick: () => onOpen(r.id),
          cells: [
            <span className="vat-party" key="t">
              <span>
                {r.priority === 'high' && !r.done ? <Icon name="alert" size={14} /> : null} {r.title}
              </span>
              <span className="muted small">{taskName(r.code, r.number)}</span>
            </span>,
            r.done ? <StatusBadge key="s" tone="ok" icon="checkCircle" label={r.stage_name} /> : r.stage_name,
            r.assignee_name ?? <span className="muted">—</span>,
            r.deadline ? (
              <span key="d" className={!r.done && r.deadline < today ? 'tk-warn' : ''}>
                {dayText(r.deadline)}
              </span>
            ) : (
              '—'
            ),
            <span key="h" className="tabular">
              {r.estimate ? `${hm(r.spent)} / ${hm(r.estimate)}` : hm(r.spent)}
            </span>,
          ],
        }))}
      />
    </section>
  );
}

function Milestones({ p, onChange }: { p: Project; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const [edit, setEdit] = useState<{ id?: string; name: string; due: string } | null>(null);
  const manage = can('project.manage');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <p className="muted small">{t('tk.milestones_hint')}</p>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEdit({ name: '', due: '' })}>
            {t('tk.new_milestone')}
          </Button>
        ) : null}
      </div>
      {p.milestones.length ? (
        <ol className="tk-milestones">
          {p.milestones.map((m) => (
            <li key={m.id} className={m.reached_at ? 'reached' : m.due && m.due < p.today ? 'late' : ''}>
              <span className="tk-ms-dot" aria-hidden="true">
                <Icon name={m.reached_at ? 'check' : 'star'} size={14} />
              </span>
              <span className="vat-party">
                <strong>{m.name}</strong>
                <span className="muted small">
                  {[
                    m.reached_at ? t('tk.reached', { at: dateTime(m.reached_at) }) : m.due ? t('tk.due', { day: dayText(m.due) }) : t('tk.no_date'),
                    m.tasks ? t('tk.tasks_done', { done: m.done, total: m.tasks }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {m.tasks ? <Progress value={m.done} max={m.tasks} label={t('tk.tasks_done', { done: m.done, total: m.tasks })} /> : null}
              </span>
              {manage ? (
                <span className="row-actions">
                  <Button
                    size="sm"
                    variant={m.reached_at ? 'ghost' : undefined}
                    icon={m.reached_at ? 'undo' : 'check'}
                    busy={busy}
                    onClick={() =>
                      void run(async () => {
                        await action('project.reach_milestone', { id: m.id, reached: !m.reached_at });
                        await onChange();
                      })
                    }
                  >
                    {m.reached_at ? t('tk.not_reached') : t('tk.mark_reached')}
                  </Button>
                  <IconButton icon="edit" label={t('tk.edit')} onClick={() => setEdit({ id: m.id, name: m.name, due: m.due ?? '' })} />
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState icon="star" title={t('tk.no_milestones')} body={t('tk.no_milestones_hint')} />
      )}
      {edit ? (
        <Dialog
          title={edit.id ? t('tk.edit_milestone') : t('tk.new_milestone')}
          onClose={() => setEdit(null)}
          size="sm"
          footer={
            <>
              {edit.id ? (
                <Button
                  variant="ghost"
                  icon="trash"
                  onClick={() =>
                    void run(async () => {
                      await action('project.remove_milestone', { id: edit.id });
                      setEdit(null);
                      await onChange();
                    })
                  }
                >
                  {t('tk.remove')}
                </Button>
              ) : null}
              <span className="foot-actions">
                <Button
                  variant="primary"
                  icon="check"
                  busy={busy}
                  disabled={!edit.name.trim()}
                  onClick={() =>
                    void run(async () => {
                      await action('project.save_milestone', { id: edit.id ?? null, project: p.id, name: edit.name.trim(), due: edit.due || null });
                      setEdit(null);
                      await onChange();
                    }, t('tk.saved'))
                  }
                >
                  {t('tk.save')}
                </Button>
              </span>
            </>
          }
        >
          <div className="form-stack">
            <Field label={t('tk.name')} required>
              <input className="input" value={edit.name} maxLength={120} placeholder={t('tk.milestone_example')} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-autofocus />
            </Field>
            <Field label={t('tk.due_day')} hint={t('tk.optional')}>
              <input className="input" type="date" value={edit.due} onChange={(e) => setEdit({ ...edit, due: e.target.value })} />
            </Field>
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

function ProjectTime({ p, setup, onChange }: { p: Project; setup: Setup | null; onChange: () => Promise<void> }) {
  const [log, setLog] = useState(false);
  const can = setup?.projects.some((x) => x.id === p.id) && p.state === 'active';
  return (
    <div className="shop-detail">
      <section className="panel">
        <div className="prot-history-head">
          <h2 className="rep-sub">{t('tk.team')}</h2>
          {can ? (
            <Button size="sm" icon="plus" onClick={() => setLog(true)}>
              {t('tk.log_time')}
            </Button>
          ) : null}
        </div>
        <Table
          head={[t('tk.person'), t('tk.r_hours'), t('tk.open_tasks')]}
          left={[0]}
          empty={t('tk.no_team')}
          rows={p.team.map((m) => ({ key: m.id, cells: [m.name, hm(m.minutes), String(m.open)] }))}
        />
      </section>
      <section className="panel">
        <h2 className="rep-sub">{p.billing === 'none' ? t('tk.not_billed') : t('tk.billing')}</h2>
        {p.billing === 'none' ? (
          <p className="muted">{t('tk.internal_hint')}</p>
        ) : (
          <>
            <p>{p.billing === 'hours' ? t('tk.by_hours', { rate: money(p.hourly_rate) }) : t('tk.fixed_of', { price: money(p.fixed_price) })}</p>
            {p.billing === 'hours' ? <p className="muted small">{t('tk.unbilled_now', { time: hm(p.unbilled), amount: money(Math.round((p.unbilled * p.hourly_rate) / 60)) })}</p> : null}
            {p.bills.length ? (
              <ul className="shop-ways">
                {p.bills.map((b) => (
                  <li key={b.id}>
                    <span className="vat-party">
                      <span>{b.label}</span>
                      <span className="muted small">{[b.name ?? t('tk.draft'), dateTime(b.at), b.state === 'cancelled' ? t('tk.cancelled_invoice') : null].filter(Boolean).join(' · ')}</span>
                    </span>
                    <strong className="tabular">{money(b.amount)}</strong>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="muted small">{t('tk.invoiced_total', { amount: money(p.invoiced) })}</p>
          </>
        )}
      </section>
      {log && setup ? (
        <TimeDialog
          initial={{ project: p.id, day: todayIso() }}
          projects={setup.projects.filter((x) => x.id === p.id)}
          onClose={() => setLog(false)}
          onSaved={async () => {
            setLog(false);
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function ProjectSettings({ p, onChange }: { p: Project; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [cols, setCols] = useState(p.stages.map((s) => ({ id: s.id as string | null, name: s.name, done: s.done, tasks: s.tasks })));
  const [state, setState] = useState<PState | null>(null);
  const move = (i: number, d: -1 | 1) => {
    const next = [...cols];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    setCols(next);
  };
  const changedCols = JSON.stringify(cols.map(({ id, name, done }) => [id, name, done])) !== JSON.stringify(p.stages.map((s) => [s.id, s.name, s.done]));
  return (
    <div className="shop-detail">
      <section className="panel form-stack">
        <div>
          <h2 className="rep-sub">{t('tk.columns')}</h2>
          <p className="muted small">{t('tk.columns_hint')}</p>
        </div>
        <ol className="tk-cols-edit">
          {cols.map((c, i) => (
            <li key={c.id ?? `new-${i}`}>
              <input
                className="input"
                value={c.name}
                maxLength={40}
                aria-label={t('tk.column_name', { n: i + 1 })}
                onChange={(e) => setCols(cols.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))}
              />
              <label className="check-row small">
                <input type="checkbox" checked={c.done} disabled={i === 0} onChange={(e) => setCols(cols.map((x, k) => (k === i ? { ...x, done: e.target.checked } : x)))} />
                <span>{t('tk.counts_done')}</span>
              </label>
              <span className="row-actions">
                <IconButton icon="up" label={t('tk.move_up')} disabled={i === 0} onClick={() => move(i, -1)} />
                <IconButton icon="down" label={t('tk.move_down')} disabled={i === cols.length - 1} onClick={() => move(i, 1)} />
                <IconButton
                  icon="trash"
                  label={c.tasks ? t('tk.has_tasks', { n: c.tasks }) : t('tk.remove')}
                  disabled={cols.length <= 2 || c.tasks > 0}
                  onClick={() => setCols(cols.filter((_, k) => k !== i))}
                />
              </span>
            </li>
          ))}
        </ol>
        <div className="row-actions">
          <Button size="sm" variant="ghost" icon="plus" disabled={cols.length >= 12} onClick={() => setCols([...cols.slice(0, -1), { id: null, name: '', done: false, tasks: 0 }, ...cols.slice(-1)])}>
            {t('tk.add_column')}
          </Button>
          <span className="foot-actions">
            <Button
              variant="primary"
              icon="check"
              busy={busy}
              disabled={!changedCols || cols.some((c) => !c.name.trim())}
              onClick={() =>
                void run(async () => {
                  await action('project.save_stages', { project: p.id, stages: cols.map((c) => ({ id: c.id, name: c.name.trim(), done: c.done })) });
                  await onChange();
                }, t('tk.saved'))
              }
            >
              {t('tk.save_columns')}
            </Button>
          </span>
        </div>
      </section>
      <section className="panel form-stack">
        <div>
          <h2 className="rep-sub">{t('tk.project_state')}</h2>
          <p className="muted small">{t('tk.project_state_hint')}</p>
        </div>
        <div className="tk-states">
          {(['active', 'on_hold', 'done', 'cancelled'] as PState[]).map((s) => (
            <Button key={s} variant={p.state === s ? 'primary' : 'ghost'} icon={STATE[s].icon} disabled={p.state === s} onClick={() => setState(s)}>
              {t(`tk.st_${s}` as MessageKey)}
            </Button>
          ))}
        </div>
      </section>
      {state ? (
        <Dialog
          title={t('tk.state_title', { state: t(`tk.st_${state}` as MessageKey) })}
          onClose={() => setState(null)}
          size="sm"
          footer={
            <Button
              variant={state === 'cancelled' ? 'danger' : 'primary'}
              icon="check"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('project.set_state', { id: p.id, state });
                  setState(null);
                  await onChange();
                }, t('tk.saved'))
              }
            >
              {t('tk.confirm')}
            </Button>
          }
        >
          <p>{t(`tk.state_ask_${state}` as MessageKey)}</p>
        </Dialog>
      ) : null}
    </div>
  );
}

// ---- forms --------------------------------------------------------------------------------------------

function ProjectForm({ setup, project, onClose, onSaved }: { setup: Setup; project?: Project; onClose: () => void; onSaved: (id: string) => void }) {
  const { me } = useSession();
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: project?.name ?? '',
    code: project?.code ?? '',
    customer: project?.customer ?? '',
    manager: project?.manager ?? me?.userId ?? '',
    members: project?.members ?? ([] as string[]),
    starts: project?.starts ?? todayIso(),
    deadline: project?.deadline ?? '',
    budget: project?.budget_minutes ? String(project.budget_minutes / 60) : '',
    billing: (project?.billing ?? 'none') as Billing,
    rate: project?.hourly_rate ? (project.hourly_rate / 100).toFixed(2) : '',
    price: project?.fixed_price ? (project.fixed_price / 100).toFixed(2) : '',
    tax: project?.tax_rate ?? standardRateNow(),
    description: project?.description ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  // A code is suggested from the name until it is typed.
  const [codeTyped, setCodeTyped] = useState(!!project);
  const suggest = (name: string) =>
    name
      .normalize('NFD')
      .replace(/[^A-Za-z0-9 ]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .map((w, i, all) => (all.length === 1 ? w.slice(0, 4) : w[0]))
      .join('')
      .toUpperCase()
      .slice(0, 6);
  const rate = f.rate ? parseMoney(f.rate) : null;
  const price = f.price ? parseMoney(f.price) : null;
  const budget = f.budget ? Number(f.budget.replace(',', '.')) : 0;
  const errors = {
    name: !f.name.trim() ? t('tk.e_name') : null,
    code: !/^[A-Z][A-Z0-9]{1,5}$/.test(f.code) ? t('tk.e_code') : null,
    customer: f.billing !== 'none' && !f.customer ? t('tk.e_customer') : null,
    rate: f.billing === 'hours' && !rate ? t('tk.e_rate') : null,
    price: f.billing === 'fixed' && !price ? t('tk.e_price') : null,
    deadline: f.deadline && f.starts && f.deadline < f.starts ? t('tk.e_deadline') : null,
    budget: Number.isNaN(budget) || budget < 0 ? t('tk.e_budget') : null,
  };
  const err = (e: string | null) => (tried ? e : null);
  return (
    <Dialog
      title={project ? t('tk.edit_project') : t('tk.new_project')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (Object.values(errors).some(Boolean)) return;
              void run(async () => {
                const out = await action<{ id: string }>('project.save_project', {
                  id: project?.id ?? null,
                  name: f.name.trim(),
                  code: f.code,
                  customer: f.customer || null,
                  manager: f.manager || null,
                  members: f.members,
                  starts: f.starts || null,
                  deadline: f.deadline || null,
                  budget_hours: budget,
                  billing: f.billing,
                  hourly_rate: rate ?? 0,
                  fixed_price: price ?? 0,
                  tax_rate: f.tax,
                  description: f.description.trim() || null,
                });
                onSaved(out.id);
              }, t('tk.saved'));
            }}
          >
            {project ? t('tk.save') : t('tk.create')}
          </Button>
        </span>
      }
    >
      <div className="form-stack sub-form">
        <div className="form-grid tk-name-row">
          <Field label={t('tk.name')} required error={err(errors.name)}>
            <input
              className="input"
              value={f.name}
              maxLength={120}
              placeholder={t('tk.name_example')}
              onChange={(e) => {
                set('name', e.target.value);
                if (!codeTyped) set('code', suggest(e.target.value));
              }}
              data-autofocus
            />
          </Field>
          <Field label={t('tk.code')} hint={t('tk.code_hint')} required error={err(errors.code)}>
            <input
              className="input tk-code-input"
              value={f.code}
              maxLength={6}
              onChange={(e) => {
                setCodeTyped(true);
                set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
              }}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('tk.manager')}>
            <select className="input" value={f.manager} onChange={(e) => set('manager', e.target.value)}>
              {setup.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('tk.customer')} hint={t('tk.customer_hint')} error={err(errors.customer)}>
            <select className="input" value={f.customer} onChange={(e) => set('customer', e.target.value)}>
              <option value="">{t('tk.internal')}</option>
              {setup.customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <fieldset className="option-group">
          <legend>{t('tk.team')}</legend>
          <p className="muted small">{t('tk.team_hint')}</p>
          <div className="tk-members">
            {setup.people
              .filter((p) => p.id !== f.manager)
              .map((p) => (
                <label key={p.id} className="check-row">
                  <input type="checkbox" checked={f.members.includes(p.id)} onChange={(e) => set('members', e.target.checked ? [...f.members, p.id] : f.members.filter((m) => m !== p.id))} />
                  <span>{p.name}</span>
                </label>
              ))}
          </div>
        </fieldset>
        <div className="form-grid">
          <Field label={t('tk.starts')}>
            <input className="input" type="date" value={f.starts} onChange={(e) => set('starts', e.target.value)} />
          </Field>
          <Field label={t('tk.deadline')} error={err(errors.deadline)}>
            <input className="input" type="date" value={f.deadline} onChange={(e) => set('deadline', e.target.value)} />
          </Field>
          <Field label={t('tk.budget_hours')} hint={t('tk.budget_hint')} error={err(errors.budget)}>
            <input className="input" inputMode="decimal" value={f.budget} onChange={(e) => set('budget', e.target.value)} />
          </Field>
        </div>
        <fieldset className="option-group">
          <legend>{t('tk.billing')}</legend>
          <div className="tk-billing" role="radiogroup">
            {(['none', 'hours', 'fixed'] as Billing[]).map((b) => (
              <label key={b} className={`option-card${f.billing === b ? ' selected' : ''}`}>
                <input type="radio" name="billing" checked={f.billing === b} onChange={() => set('billing', b)} />
                <span className="vat-party">
                  <strong>{t(`tk.bill_${b}` as MessageKey)}</strong>
                  <span className="muted small">{t(`tk.bill_${b}_hint` as MessageKey)}</span>
                </span>
              </label>
            ))}
          </div>
          {f.billing !== 'none' ? (
            <div className="form-grid">
              {f.billing === 'hours' ? (
                <Field label={t('tk.rate')} hint={t('tk.before_vat')} error={err(errors.rate)}>
                  <input className="input" inputMode="decimal" value={f.rate} onChange={(e) => set('rate', e.target.value)} />
                </Field>
              ) : (
                <Field label={t('tk.price')} hint={t('tk.before_vat')} error={err(errors.price)}>
                  <input className="input" inputMode="decimal" value={f.price} onChange={(e) => set('price', e.target.value)} />
                </Field>
              )}
              <Field label={t('tk.vat_rate')}>
                <TaxRateSelect value={f.tax} onChange={(r) => set('tax', r)} />
              </Field>
            </div>
          ) : null}
        </fieldset>
        <Field label={t('tk.description')} hint={t('tk.optional')}>
          <textarea className="input textarea" rows={3} value={f.description} maxLength={4000} onChange={(e) => set('description', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function TaskForm({
  project,
  stage,
  task,
  people,
  onClose,
  onSaved,
}: {
  project: Pick<Project, 'id' | 'code' | 'stages' | 'milestones' | 'team'>;
  stage?: string;
  task?: TaskDetail;
  people: Setup['people'];
  onClose: () => void;
  onSaved: (id: string) => Promise<void>;
}) {
  const { busy, run } = useSave();
  const { me } = useSession();
  const [f, setF] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    stage: task?.stage ?? stage ?? project.stages[0]!.id,
    assignee: task ? (task.assignee ?? '') : '',
    priority: task?.priority ?? 'normal',
    start: task?.start ?? '',
    deadline: task?.deadline ?? '',
    estimate: task?.estimate ? durationText(task.estimate) : '',
    checklist: task?.checklist.map((c) => c.text) ?? ([] as string[]),
    milestone: task?.milestone ?? '',
  });
  const [line, setLine] = useState('');
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const estimate = f.estimate ? parseDuration(f.estimate) : 0;
  // The team first, then everyone else who can work on projects.
  const team = new Set(project.team.map((m) => m.id));
  const choices = [...people].sort((a, b) => Number(team.has(b.id)) - Number(team.has(a.id)) || a.name.localeCompare(b.name));
  const milestones = 'milestones' in project ? project.milestones.filter((m) => !('reached_at' in m) || !m.reached_at || m.id === f.milestone) : [];
  return (
    <Dialog
      title={task ? t('tk.edit_task', { n: taskName(project.code, task.number) }) : t('tk.new_task')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!f.title.trim() || estimate === null) return;
              void run(async () => {
                const out = await action<{ id: string }>('project.save_task', {
                  id: task?.id ?? null,
                  project: project.id,
                  title: f.title.trim(),
                  description: f.description.trim() || null,
                  stage: f.stage,
                  assignee: f.assignee || null,
                  priority: f.priority,
                  start: f.start || null,
                  deadline: f.deadline || null,
                  estimate_hours: (estimate ?? 0) / 60,
                  checklist: f.checklist,
                  milestone: f.milestone || null,
                });
                await onSaved(out.id);
              }, t('tk.saved'));
            }}
          >
            {task ? t('tk.save') : t('tk.create')}
          </Button>
        </span>
      }
    >
      <div className="form-stack sub-form">
        <Field label={t('tk.task_title')} required error={tried && !f.title.trim() ? t('tk.e_title') : null}>
          <input className="input" value={f.title} maxLength={200} placeholder={t('tk.task_example')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('tk.assignee')}>
            <select className="input" value={f.assignee} onChange={(e) => set('assignee', e.target.value)}>
              <option value="">{t('tk.nobody')}</option>
              {me && choices.some((p) => p.id === me.userId) ? <option value={me.userId}>{t('tk.me_name', { name: me.name })}</option> : null}
              {choices
                .filter((p) => p.id !== me?.userId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {team.has(p.id) ? '' : ` (${t('tk.not_on_team')})`}
                  </option>
                ))}
            </select>
          </Field>
          <Field label={t('tk.column')}>
            <select className="input" value={f.stage} onChange={(e) => set('stage', e.target.value)}>
              {project.stages.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('tk.start')} hint={t('tk.start_hint')}>
            <input className="input" type="date" value={f.start} max={f.deadline || undefined} onChange={(e) => set('start', e.target.value)} />
          </Field>
          <Field label={t('tk.deadline')} error={f.start && f.deadline && f.start > f.deadline ? t('tk.e_start_after') : null}>
            <input className="input" type="date" value={f.deadline} min={f.start || undefined} onChange={(e) => set('deadline', e.target.value)} />
          </Field>
          <Field label={t('tk.estimate')} hint={t('tk.how_long_hint')} error={tried && estimate === null ? t('tk.e_time') : null}>
            <input className="input" inputMode="decimal" value={f.estimate} placeholder="2:00" onChange={(e) => set('estimate', e.target.value)} />
          </Field>
        </div>
        <div className="form-grid">
          {milestones.length ? (
            <Field label={t('tk.milestone')}>
              <select className="input" value={f.milestone} onChange={(e) => set('milestone', e.target.value)}>
                <option value="">{t('tk.none')}</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('tk.high')}</strong>
            <p className="muted">{t('tk.high_hint')}</p>
          </div>
          <Switch checked={f.priority === 'high'} onChange={(v) => set('priority', v ? 'high' : 'normal')} label={t('tk.high')} />
        </div>
        <Field label={t('tk.description')} counter={`${f.description.length}/8000`}>
          <textarea className="input textarea" rows={4} value={f.description} maxLength={8000} placeholder={t('tk.description_hint')} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <fieldset className="option-group">
          <legend>{t('tk.checklist')}</legend>
          {f.checklist.length ? (
            <ol className="fs-lines">
              {f.checklist.map((c, i) => (
                <li key={`${c}-${i}`}>
                  <span>{c}</span>
                  <IconButton
                    icon="x"
                    label={t('tk.remove')}
                    onClick={() =>
                      set(
                        'checklist',
                        f.checklist.filter((_, k) => k !== i),
                      )
                    }
                  />
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted small">{t('tk.no_checklist')}</p>
          )}
          <div className="mkt-check">
            <input
              className="input"
              value={line}
              maxLength={160}
              placeholder={t('tk.add_line')}
              aria-label={t('tk.add_line')}
              onChange={(e) => setLine(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && line.trim()) {
                  e.preventDefault();
                  set('checklist', [...f.checklist, line.trim()]);
                  setLine('');
                }
              }}
            />
            <Button icon="plus" disabled={!line.trim() || f.checklist.length >= 50} onClick={() => (set('checklist', [...f.checklist, line.trim()]), setLine(''))}>
              {t('tk.add')}
            </Button>
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}

// ---- one task -----------------------------------------------------------------------------------------

function TaskDialog({ id, board, onClose, onChange }: { id: string; board?: TaskRow[]; onClose: () => void; onChange: () => Promise<void> }) {
  const { me, can } = useSession();
  const { busy, run } = useSave();
  const { data: tk, reload } = useAction<TaskDetail>('project.task', { id });
  const { data: setup } = useAction<Setup>('project.setup');
  const [note, setNote] = useState('');
  const [edit, setEdit] = useState(false);
  const [log, setLog] = useState<Partial<TimeForm> | null>(null);
  const done = async () => {
    await reload();
    await onChange();
  };
  if (!tk) {
    return (
      <Dialog title={t('tk.task')} onClose={onClose} size="xl">
        <Skeleton h={360} />
      </Dialog>
    );
  }
  const open = tk.project_state === 'active' && !tk.archived;
  const editable = tk.can_edit && open;
  const mineRunning = tk.time.find((x) => x.started_at && x.person === me?.userId);
  const next = tk.stages[tk.stages.findIndex((s) => s.id === tk.stage) + 1];
  return (
    <>
      <Dialog
        title={`${taskName(tk.code, tk.number)} · ${tk.title}`}
        onClose={onClose}
        size="xl"
        footer={
          <>
            {tk.can_edit && tk.project_state === 'active' ? (
              <Button
                variant="ghost"
                icon={tk.archived ? 'undo' : 'archive'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('project.archive_task', { id: tk.id, archived: !tk.archived });
                      await done();
                    },
                    tk.archived ? t('tk.restored') : t('tk.archived'),
                  )
                }
              >
                {tk.archived ? t('tk.restore') : t('tk.archive')}
              </Button>
            ) : null}
            {editable ? (
              <Button variant="ghost" icon="edit" onClick={() => setEdit(true)}>
                {t('tk.edit')}
              </Button>
            ) : null}
            {editable && !tk.done ? (
              <Button
                icon={mineRunning ? 'pause' : 'clock'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action(mineRunning ? 'project.timer_stop' : 'project.timer_start', mineRunning ? {} : { task: tk.id });
                      await done();
                    },
                    mineRunning ? t('tk.timer_stopped') : t('tk.timer_started'),
                  )
                }
              >
                {mineRunning ? t('tk.stop') : t('tk.start_timer')}
              </Button>
            ) : null}
            {editable && !tk.done && next ? (
              <Button
                variant="primary"
                icon={next.done ? 'checkCircle' : 'chevronRight'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('project.move_task', { id: tk.id, stage: next.id });
                      await done();
                    },
                    t('tk.moved_to', { column: next.name }),
                  )
                }
              >
                {next.done ? t('tk.mark_done') : t('tk.move_to', { column: next.name })}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="form-stack mkt-dlg tk-dlg">
          <div className="item-summary">
            <div>
              <span className="muted">{t('tk.project')}</span>
              <strong>
                <Link className="link" to={`/tasks/p/${tk.project}`} onClick={onClose}>
                  {tk.project_name}
                </Link>
              </strong>
            </div>
            <div>
              <span className="muted">{t('tk.assignee')}</span>
              <strong>{tk.assignee_name ?? t('tk.nobody')}</strong>
            </div>
            <div>
              <span className="muted">{t('tk.deadline')}</span>
              <strong className={!tk.done && tk.deadline && tk.deadline < todayIso() ? 'tk-warn' : ''}>{tk.deadline ? dayText(tk.deadline) : '—'}</strong>
            </div>
            <div>
              <span className="muted">{t('tk.spent')}</span>
              <strong>{tk.estimate ? `${hm(tk.spent)} / ${hm(tk.estimate)}` : hm(tk.spent)}</strong>
            </div>
            {tk.done ? <StatusBadge tone="ok" icon="checkCircle" label={tk.stage_name} /> : tk.archived ? <StatusBadge tone="neutral" icon="archive" label={t('tk.archived_badge')} /> : null}
          </div>
          {editable ? (
            <div className="tk-stage-pick" role="radiogroup" aria-label={t('tk.column')}>
              {tk.stages.map((s) => (
                <button
                  key={s.id}
                  role="radio"
                  aria-checked={s.id === tk.stage}
                  className={`tk-stage${s.id === tk.stage ? ' active' : ''}${s.done ? ' done' : ''}`}
                  disabled={busy}
                  onClick={() =>
                    s.id !== tk.stage &&
                    void run(async () => {
                      await action('project.move_task', { id: tk.id, stage: s.id });
                      await done();
                    })
                  }
                >
                  {s.done ? <Icon name="check" size={14} /> : null}
                  {s.name}
                </button>
              ))}
            </div>
          ) : null}
          {editable && board
            ? (() => {
                // Its place in the column as the board shows it, movable without dragging.
                const col = board
                  .filter((r) => r.stage === tk.stage)
                  .sort((a, b) => a.position - b.position)
                  .map((r) => r.id);
                const i = col.indexOf(tk.id);
                return (
                  <Reorder
                    index={i}
                    count={col.length}
                    busy={busy}
                    onMove={(by) => {
                      const order = [...col];
                      [order[i], order[i + by]] = [order[i + by]!, order[i]!];
                      void run(async () => {
                        await action('project.move_task', { id: tk.id, stage: tk.stage, order });
                        await done();
                      });
                    }}
                  />
                );
              })()
            : null}
          {mineRunning ? (
            <p className="callout callout-info">
              <Icon name="clock" size={16} />
              <span>{t('tk.timer_on', { time: hm(Math.floor((Date.now() - Date.parse(mineRunning.started_at!)) / 60_000)) })}</span>
            </p>
          ) : null}
          <TaskLinks tk={tk} editable={editable} onChange={done} />
          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('tk.description')}</h3>
              {tk.description ? <p className="fs-note">{tk.description}</p> : <p className="muted">{t('tk.no_description')}</p>}
              {tk.milestone_name ? (
                <p className="small">
                  <Icon name="star" size={14} /> {tk.milestone_name}
                </p>
              ) : null}
              <h3 className="rep-sub">{t('tk.comments')}</h3>
              {tk.comments.length ? (
                <ul className="tk-comments">
                  {tk.comments.map((c) => (
                    <li key={c.id} className={c.by === me?.userId ? 'mine' : ''}>
                      <span className="tk-avatar" aria-hidden="true">
                        {(c.by_name ?? '?').slice(0, 1)}
                      </span>
                      <div>
                        <span className="small">
                          <strong>{c.by_name}</strong> <span className="muted">{dateTime(c.at)}</span>
                        </span>
                        <p className="fs-note">{c.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('tk.no_comments')}</p>
              )}
              {tk.can_edit ? (
                <div className="tk-comment-box">
                  <textarea
                    className="input textarea"
                    rows={2}
                    value={note}
                    maxLength={4000}
                    placeholder={t('tk.write_comment')}
                    aria-label={t('tk.write_comment')}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <Button
                    size="sm"
                    icon="send"
                    busy={busy}
                    disabled={!note.trim()}
                    onClick={() =>
                      void run(async () => {
                        await action('project.comment', { task: tk.id, text: note.trim() });
                        setNote('');
                        await done();
                      })
                    }
                  >
                    {t('tk.send')}
                  </Button>
                </div>
              ) : null}
            </section>
            <section>
              <div className="fs-sec-head">
                <h3 className="rep-sub">{t('tk.checklist')}</h3>
                {tk.checklist.length ? <span className="muted small">{t('tk.ticked', { n: tk.checklist.filter((c) => c.done).length, total: tk.checklist.length })}</span> : null}
              </div>
              {tk.checklist.length ? (
                <ul className="fs-checks">
                  {tk.checklist.map((c, i) => (
                    <li key={i}>
                      <label className="check-row">
                        <input
                          type="checkbox"
                          checked={c.done}
                          disabled={!editable || busy}
                          onChange={(e) =>
                            void run(async () => {
                              await action('project.tick', { id: tk.id, item: i, done: e.target.checked });
                              await reload();
                            })
                          }
                        />
                        <span>{c.text}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('tk.no_checklist')}</p>
              )}
              <div className="fs-sec-head">
                <h3 className="rep-sub">{t('tk.time')}</h3>
                {editable && setup?.projects.some((x) => x.id === tk.project) ? (
                  <Button size="sm" variant="ghost" icon="plus" onClick={() => setLog({ project: tk.project, task: tk.id, day: todayIso(), billable: tk.billing === 'hours' })}>
                    {t('tk.log_time')}
                  </Button>
                ) : null}
              </div>
              {tk.time.length ? (
                <ul className="shop-ways">
                  {tk.time.map((x) => (
                    <li key={x.id}>
                      <span className="vat-party">
                        <span>{x.person_name}</span>
                        <span className="muted small">
                          {[dayText(x.day), x.note, x.started_at ? t('tk.running') : null, x.invoice ? t('tk.invoiced') : tk.billing === 'hours' && !x.billable ? t('tk.not_billable') : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <strong className="tabular">{x.started_at ? '…' : hm(x.minutes)}</strong>
                      {editable && !x.invoice && !x.started_at && (x.person === me?.userId || can('project.manage')) ? (
                        <IconButton
                          icon="edit"
                          label={t('tk.edit')}
                          onClick={() => setLog({ id: x.id, project: tk.project, task: tk.id, day: x.day, time: durationText(x.minutes), note: x.note ?? '', billable: x.billable })}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('tk.no_time')}</p>
              )}
            </section>
          </div>
          {tk.created_by_name ? <p className="muted small">{t('tk.created_by', { name: tk.created_by_name })}</p> : null}
        </div>
      </Dialog>
      {edit && setup ? (
        <TaskEditor
          projectId={tk.project}
          task={tk}
          people={setup.people}
          onClose={() => setEdit(false)}
          onSaved={async () => {
            setEdit(false);
            await done();
          }}
        />
      ) : null}
      {log && setup ? (
        <TimeDialog
          initial={log}
          projects={setup.projects.filter((x) => x.id === tk.project)}
          fixedTask={tk.id}
          onClose={() => setLog(null)}
          onSaved={async () => {
            setLog(null);
            await done();
          }}
        />
      ) : null}
    </>
  );
}

/** Loads the project, then edits one of its tasks. */
function TaskEditor({ projectId, ...rest }: { projectId: string } & Omit<Parameters<typeof TaskForm>[0], 'project'>) {
  const { data: p } = useAction<Project>('project.project', { id: projectId });
  return p ? <TaskForm project={p} {...rest} /> : null;
}

/** What a task waits for and what waits for it; the team adds and removes the links. */
function TaskLinks({ tk, editable, onChange }: { tk: TaskDetail; editable: boolean; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: all } = useAction<TaskRow[]>('project.tasks', { project: tk.project });
  const [pick, setPick] = useState('');
  const taken = new Set([tk.id, ...tk.blocked_by.map((b) => b.id), ...tk.blocks.map((b) => b.id)]);
  const choices = (all ?? []).filter((x) => !taken.has(x.id) && !x.archived);
  if (!editable && !tk.blocked_by.length && !tk.blocks.length) return null;
  const open = tk.blocked_by.filter((b) => !b.done);
  return (
    <section className="tk-links" aria-labelledby="tk-links-title">
      <h3 id="tk-links-title" className="rep-sub">
        {t('tk.links')}
      </h3>
      {open.length && !tk.done ? (
        <p className="callout callout-warn">
          <Icon name="lock" size={16} /> {t('tk.blocked_note', { list: open.map((b) => b.name).join(', ') })}
        </p>
      ) : null}
      <div className="tk-links-grid">
        <div>
          <span className="muted small">{t('tk.waits_for')}</span>
          {tk.blocked_by.length ? (
            <ul className="tk-link-list">
              {tk.blocked_by.map((b) => (
                <li key={b.id}>
                  <Icon name={b.done ? 'checkCircle' : 'circle'} size={14} />
                  <span className={b.done ? 'muted' : ''}>
                    {b.name} · {b.title}
                  </span>
                  {editable ? (
                    <IconButton
                      icon="x"
                      label={t('tk.unlink', { name: b.name })}
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await action('project.remove_link', { task: tk.id, blocked_by: b.id });
                          await onChange();
                        })
                      }
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">{t('tk.waits_for_none')}</p>
          )}
          {editable && choices.length ? (
            <div className="tk-link-add">
              <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('tk.add_wait')}>
                <option value="">{t('tk.add_wait')}</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {taskName(c.code, c.number)} · {c.title}
                  </option>
                ))}
              </select>
              <Button
                icon="plus"
                disabled={!pick}
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    await action('project.add_link', { task: tk.id, blocked_by: pick });
                    setPick('');
                    await onChange();
                  }, t('tk.linked'))
                }
              >
                {t('tk.add')}
              </Button>
            </div>
          ) : null}
        </div>
        <div>
          <span className="muted small">{t('tk.blocks')}</span>
          {tk.blocks.length ? (
            <ul className="tk-link-list">
              {tk.blocks.map((b) => (
                <li key={b.id}>
                  <Icon name={b.done ? 'checkCircle' : 'circle'} size={14} />
                  <span className={b.done ? 'muted' : ''}>
                    {b.name} · {b.title}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">{t('tk.blocks_none')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
